import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faTruck } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/LanguageContext';

const ServicesContainer = styled.section`
  position: relative;
  padding: 120px 20px;
  background: linear-gradient(180deg, rgba(247, 248, 252, 0.7) 0%, rgba(236, 240, 251, 0.95) 100%);

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 72px 16px;
  }
`;

const SectionHeading = styled.h2`
  text-align: center;
  font-size: clamp(2.2rem, 3vw, 3rem);
  font-weight: 700;
  color: var(--secondary);
  margin-bottom: 16px;
`;

const SectionIntro = styled.p`
  max-width: 700px;
  margin: 0 auto 60px;
  text-align: center;
  color: var(--text-muted);
  line-height: 1.7;
  font-size: 1.05rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;
  max-width: 1180px;
  margin: 0 auto;
`;

const ServiceCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid rgba(226, 232, 240, 0.7);
  box-shadow: var(--shadow-soft);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(12, 77, 162, 0.35);
    box-shadow: var(--shadow-hover);
  }

  @media (max-width: 600px) {
    padding: 6px 4px;
  }
`;

const ServiceHeader = styled.div`
  padding: 28px;
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 600px) {
    padding: 24px 22px;
    gap: 14px;
  }
`;

const ServiceIcon = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(12, 77, 162, 0.12) 0%, rgba(12, 77, 162, 0.04) 100%);
  color: var(--primary);
  font-size: 28px;

  @media (max-width: 600px) {
    width: 54px;
    height: 54px;
    font-size: 24px;
  }
`;

const ServiceTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ServiceTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--secondary);
`;

const ServiceTag = styled.span`
  font-size: 12px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--primary);
`;

const ServiceContent = styled.div`
  padding: 0 28px 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 22px;

  @media (max-width: 600px) {
    padding: 0 22px 24px;
    gap: 18px;
  }
`;

const ServiceDescription = styled.p`
  color: var(--text-muted);
  line-height: 1.7;
  margin: 0;
  font-size: 0.98rem;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const ServiceList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceItem = styled.li`
  position: relative;
  padding-left: 18px;
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.6;

  &:before {
    content: '';
    position: absolute;
    top: 9px;
    left: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary);
  }
`;

const ServiceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
  font-size: 0.95rem;
  transition: gap 0.2s ease;

  @media (max-width: 600px) {
    font-size: 0.92rem;
  }

  &:after {
    content: '→';
    font-size: 1.1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    gap: 12px;

    &:after {
      transform: translateX(4px);
    }
  }
`;

const ServicesSection = () => {
  const { t } = useLanguage();
  const services = t('home.services.items');

  return (
    <ServicesContainer>
      <SectionHeading>{t('home.services.heading')}</SectionHeading>
      <SectionIntro>
        {t('home.services.intro')}
      </SectionIntro>
      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard key={index}>
            <ServiceHeader>
              <ServiceIcon>
                <FontAwesomeIcon icon={index === 0 ? faClipboardList : faTruck} />
              </ServiceIcon>
              <ServiceTitleGroup>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceTag>{service.tag}</ServiceTag>
              </ServiceTitleGroup>
            </ServiceHeader>
            <ServiceContent>
              <ServiceDescription>{service.description}</ServiceDescription>
              {service.highlights && (
                <ServiceList>
                  {service.highlights.map((item) => (
                    <ServiceItem key={item}>{item}</ServiceItem>
                  ))}
                </ServiceList>
              )}
              {service.linkLabel && (
                <ServiceLink href={index === 0 ? '/services/conseils' : '/services/routiers'}>
                  {service.linkLabel}
                </ServiceLink>
              )}
            </ServiceContent>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </ServicesContainer>
  );
};

export default ServicesSection;